// Silent session validation when the native app resumes from a real
// background. Replaces the old "router.reload() on every foreground" behavior,
// which flashed the navigation progress bar + skeleton on any transient
// interruption (Control Center, notification shade, permission / Face ID
// sheets, the app switcher) — the "app refreshes for no reason" bug.
//
// See app/frontend/lib/capacitor.js for the pause/resume wiring.

// The default-locale (unprefixed) path always resolves, so the native app can
// call it regardless of the user's UI locale. The response is a bare status.
export const SESSION_CHECK_PATH = '/app/session-check';

// Skip the probe for brief backgrounds (rapid app-switching to copy something,
// answer a glance-notification, etc.). Real session expiry is measured in days
// — the cookie lives 14 — so a one-minute floor never misses a genuine lapse
// while eliminating a network request on every quick out-and-back.
export const MIN_BACKGROUND_MS = 60_000;

// Probe GET /app/session-check: 204 = live session, 401 = expired. On expiry
// send the user to the login page — where an Inertia visit with an expired
// session lands too (the server redirects it there). A live session resumes
// with zero UI. Network errors (offline resume) are swallowed — the next real
// navigation will surface any auth problem the same way.
//
// - `signedIn`: whether the last page had a signed-in user. A signed-out app
//   (login, signup, forgot-password) has no session to lose, and redirecting
//   it would bounce a user off the signup form to the login page.
// - `loginPath`: the localized login route from the last page's shared props,
//   so a Polish user lands on /pl/logowanie, not the English /login.
// - `shouldRedirect`: asked after the probe returns. False when a deep link
//   (e.g. "Open in Bosko" after a password reset, which ends every session)
//   brought the app back — its own visit already goes to the right page, and a
//   hard redirect here would override it.
//
// Returns a status string ('skipped' | 'signed-out' | 'valid' | 'expired' |
// 'deep-link' | 'network-error') purely so it can be unit-tested; callers
// ignore it.
export async function checkSessionOnResume({
  awayMs,
  signedIn = true,
  loginPath = '/login',
  shouldRedirect = () => true,
  fetchImpl = (...args) => fetch(...args),
  redirect = (url) => {
    window.location.href = url;
  },
} = {}) {
  if (awayMs < MIN_BACKGROUND_MS) return 'skipped';
  if (!signedIn) return 'signed-out';

  let res;
  try {
    res = await fetchImpl(SESSION_CHECK_PATH, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
  } catch {
    return 'network-error';
  }

  if (res.status === 401) {
    if (!shouldRedirect()) return 'deep-link';
    redirect(loginPath);
    return 'expired';
  }

  return 'valid';
}
